/**
 * Financial API client for the Unified Financial API (v2).
 *
 * All endpoints map to backend routes prefixed with /api/v2/financial/*.
 * The axios base URL is http://localhost:8000/api, so paths here are
 * relative to that — e.g. /v2/financial/company/{name}.
 */

import apiClient from '../api';
import { getWebhookToken } from '../webhookToken';
import { sanitizeSearchQuery } from '../sanitizeQuery';
import type {
  UnifiedFinancialResponse,
  CompanySummaryResponse,
  CompetitorComparisonResponse,
  ProjectFinancialResponse,
  SankeyRequest,
  SankeyResponse,
  SensitivityRequest,
  SensitivityResponse,
  CombinedSensitivityResponse,
  CountryConfig,
  SupportedCountriesResponse,
  CacheStatsResponse,
  EVInsightsRequest,
  EVInsightsResponse,
  EnhancedSensitivityRequest,
  EnhancedSensitivityResponse,
  CombinedAdjustedSensitivityRequest,
  CombinedAdjustedSensitivityResponse,
  SpeedometerSensitivityRequest,
  SpeedometerSensitivityResponse,
  MetricCardsResponse,
  DashboardTabResponse,
  ProjectDashboardTabResponse,
  CompanyTrendResponse,
  CompanyCompareResponse,
  FinancialMetricsComparisonRequest,
  FinancialMetricsComparisonResponse,
  BulkSummaryResponse,
} from '../../types/financial';

// ============================================================================
// Company Financials
// ============================================================================

/**
 * Get full unified financial time series for a company.
 *
 * Returns all P&L, balance sheet, EV decomposition, CAGR metrics, and
 * RegionConfig with currency units and fiscal year format.
 */
export const getCompanyFinancials = async (
  companyName: string,
  years: number = 10,
  country?: string
): Promise<UnifiedFinancialResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());
  if (country) params.append('country', country);

  const response = await apiClient.get<UnifiedFinancialResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get lightweight financial summary for a company (for list cards/badges).
 *
 * Returns only the latest year's key KPIs: EBITDA margin, ROIC, WACC,
 * Revenue, EV/EBITDA, and RegionConfig.
 */
export const getCompanySummary = async (
  companyName: string,
  country?: string
): Promise<CompanySummaryResponse> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);

  const response = await apiClient.get<CompanySummaryResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/summary?${params.toString()}`
  );
  return response.data;
};

/**
 * Search companies stored in the database (Neo4j). Used by the competitive
 * landscape "Add Competitor" dialog to surface companies already ingested,
 * in addition to any external (SEC/NSE) results.
 *
 * Backend route: GET /api/finance/companies/search?q=&limit=
 */
export interface DbCompanySearchResult {
  company_name: string;
  sector: string;
  country: 'INDIA' | 'US' | string;
  years_available: number[];
  latest_year: number | null;
  years_count: number;
}

export const searchCompaniesInDatabase = async (
  q: string,
  limit: number = 10
): Promise<{ results: DbCompanySearchResult[]; count: number }> => {
  // Sanitise the query so the backend receives only letters/digits/spaces.
  const sanitized = sanitizeSearchQuery(q);
  if (!sanitized) {
    return { results: [], count: 0 };
  }
  const params = new URLSearchParams();
  params.append('q', sanitized);
  params.append('limit', limit.toString());
  const response = await apiClient.get<{ results: DbCompanySearchResult[]; count: number }>(
    `/finance/companies/search?${params.toString()}`
  );
  return response.data;
};

/**
 * Get lightweight financial summaries for multiple companies in one request.
 *
 * Backend route: POST /api/v2/financial/companies/getAllSummary
 * Returns a map of company name → summary (or error entry for missing companies).
 */
export const getAllSummary = async (
  companies: string[],
  country?: string
): Promise<BulkSummaryResponse> => {
  const response = await apiClient.post<BulkSummaryResponse>(
    '/v2/financial/companies/getAllSummary',
    { companies, country }
  );
  return response.data;
};

// ============================================================================
// Company Competitors
// ============================================================================

export interface CompanyCompetitor {
  name: string;
  industry?: string | null;
  market_position?: string | null;
}

export interface CompanyCompetitorsResponse {
  company_name: string;
  competitors: CompanyCompetitor[];
}

/**
 * Get the auto-derived competitor list for a company.
 *
 * Backend reads from Neo4j first, falls back to same-sector lookup and
 * then CompetitionAgent. Cached server-side for 24h.
 *
 * Backend route: GET /api/v2/financial/company/{name}/competitors
 */
export const getCompanyCompetitors = async (
  companyName: string,
  topN: number = 4,
  refresh: boolean = false
): Promise<CompanyCompetitorsResponse> => {
  const params = new URLSearchParams();
  params.append('top_n', topN.toString());
  if (refresh) params.append('refresh', 'true');

  const response = await apiClient.get<CompanyCompetitorsResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/competitors?${params.toString()}`,
    { timeout: 120000 }
  );
  return response.data;
};

export interface SectorPeer {
  name: string;
  industry?: string | null;
  country?: string | null;
  company_id?: string | null;
  years_count: number;
  market_position?: string | null;
}

export interface SectorPeersResponse {
  company_name: string;
  peers: SectorPeer[];
}

/**
 * List all companies in the same sector as the focal company.
 *
 * Backend route: GET /api/v2/financial/company/{name}/sector-peers
 */
export const getSectorPeers = async (
  companyName: string,
  limit: number = 50
): Promise<SectorPeersResponse> => {
  const response = await apiClient.get<SectorPeersResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/sector-peers?limit=${limit}`
  );
  return response.data;
};

/**
 * Add a competitor to a company's persisted list.
 *
 * Backend route: POST /api/v2/financial/company/{name}/competitors
 */
export const addCompanyCompetitor = async (
  companyName: string,
  competitorName: string
): Promise<CompanyCompetitorsResponse> => {
  const response = await apiClient.post<CompanyCompetitorsResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/competitors`,
    { name: competitorName }
  );
  return response.data;
};

/**
 * Remove a competitor from a company's persisted list.
 *
 * Backend route: DELETE /api/v2/financial/company/{name}/competitors/{peer}
 */
export const removeCompanyCompetitor = async (
  companyName: string,
  competitorName: string
): Promise<CompanyCompetitorsResponse> => {
  const response = await apiClient.delete<CompanyCompetitorsResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/competitors/${encodeURIComponent(competitorName)}`
  );
  return response.data;
};

// ============================================================================
// Project Financials
// ============================================================================

/**
 * Get financial data for all companies in a project.
 */
export const getProjectFinancials = async (
  projectId: string,
  years: number = 10
): Promise<ProjectFinancialResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());

  const response = await apiClient.get<ProjectFinancialResponse>(
    `/v2/financial/project/${projectId}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get competitor comparison table for all companies in a project.
 *
 * Returns side-by-side metrics: EV/EBITDA, EBITDA margin, ROIC, WACC,
 * D/E ratio, revenue CAGR, and EV breakdown components.
 */
export const getProjectComparison = async (
  projectId: string,
  selectedCompanyId?: string,
  fiscalYear?: number
): Promise<CompetitorComparisonResponse> => {
  const params = new URLSearchParams();
  if (selectedCompanyId) params.append('selected_company_id', selectedCompanyId);
  if (fiscalYear) params.append('fiscal_year', fiscalYear.toString());

  const response = await apiClient.get<CompetitorComparisonResponse>(
    `/v2/financial/project/${projectId}/comparison?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// Sankey Chart
// ============================================================================

/**
 * Generate a Sankey (flow) chart for a company's revenue-to-EV decomposition.
 *
 * Note: The backend route prefix is /sankey (no /api prefix).
 * If the base URL is http://localhost:8000/api, this call will resolve to
 * http://localhost:8000/api/sankey/generateChart.
 * Ensure the backend route prefix is updated to /api/sankey if needed.
 */
export const generateSankeyChart = async (
  request: SankeyRequest
): Promise<SankeyResponse> => {
  const response = await apiClient.post<SankeyResponse>(
    '/sankey/generateChart',
    request
  );
  return response.data;
};

// ============================================================================
// Sensitivity Analysis
// ============================================================================

/**
 * Calculate sensitivity analysis (2-variable heatmap).
 *
 * Returns a 2D grid of EV values across revenue % and EBITDA margin % ranges.
 */
export const calculateSensitivity = async (
  request: SensitivityRequest
): Promise<SensitivityResponse> => {
  const response = await apiClient.post<SensitivityResponse>(
    '/sensitivity/enhanced/calculate',
    request,
    { timeout: 60000 }
  );
  return response.data;
};

/**
 * Calculate combined 5-parameter sensitivity (all-scenario grid).
 */
export const calculateCombinedSensitivity = async (
  request: SensitivityRequest
): Promise<CombinedSensitivityResponse> => {
  const response = await apiClient.post<CombinedSensitivityResponse>(
    '/sensitivity/enhanced/combined/calculateCombined',
    request,
    { timeout: 60000 }
  );
  return response.data;
};

// ============================================================================
// Country Config
// ============================================================================

/**
 * Get list of supported country codes.
 */
export const getSupportedCountries = async (): Promise<SupportedCountriesResponse> => {
  const response = await apiClient.get<SupportedCountriesResponse>(
    '/v2/financial/countries'
  );
  return response.data;
};

/**
 * Get full country configuration (currency, fiscal year, terminology labels).
 */
export const getCountryConfig = async (
  countryCode: string
): Promise<CountryConfig> => {
  const response = await apiClient.get<CountryConfig>(
    `/v2/financial/countries/${countryCode.toUpperCase()}/config`
  );
  return response.data;
};

// ============================================================================
// Cache Stats (admin/debug)
// ============================================================================

export const getFinancialCacheStats = async (): Promise<CacheStatsResponse> => {
  const response = await apiClient.get<CacheStatsResponse>(
    '/v2/financial/cache/stats'
  );
  return response.data;
};

// ============================================================================
// EV Insights
// ============================================================================

/**
 * Run AI-powered EV and EV/EBITDA multiple analysis for a company (10-year window).
 *
 * Calls POST /evInsights — returns structured narrative insights for
 * self-performance and (optionally) competitive positioning.
 */
export const getEVInsights = async (
  request: EVInsightsRequest
): Promise<EVInsightsResponse> => {
  const response = await apiClient.post<EVInsightsResponse>(
    '/analysis/evInsights',
    request,
    { timeout: 60000 }
  );
  return response.data;
};

// ============================================================================
// India Financial Ingestion (triggers n8n webhook via /api/finance)
// ============================================================================

/**
 * Fetch India company financial data via the MoneyControl JSON API (mcapi).
 * Responds synchronously with the full result in `data` (no polling).
 */
export const triggerIndiaFinancialIngestion = async (data: {
  company_name: string;
  data_type?: 'consolidated' | 'standalone';
}): Promise<{ status: string; message: string; data?: any; job_id?: string }> => {
  const token = await getWebhookToken();
  const response = await apiClient.post(
    '/finance/india-financial-data-mcapi',
    data,
    {
      timeout: 600000,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );
  return response.data;
};

/**
 * Poll the status of an India financial ingestion job.
 */
export const pollIndiaJobStatus = async (jobId: string): Promise<{
  job_id: string;
  status: 'processing' | 'completed' | 'failed';
  company_name: string;
  data?: any;
  error?: string | null;
  created_at: string;
  completed_at?: string | null;
}> => {
  const response = await apiClient.get(
    `/finance/india-financial-data/${jobId}`,
    { timeout: 15000 }
  );
  return response.data;
};

// ============================================================================
// Enhanced Sensitivity Analysis
// ============================================================================

/**
 * Run enhanced sensitivity analysis (3-parameter: Revenue, EBITDA Margin, Invested Capital).
 *
 * The backend creates scenario_change_percent³ scenarios per year and returns
 * sensitivity indices (elasticity ranking) + all scenario EV outcomes.
 *
 * Backend route: POST /api/sensitivity/enhanced/calculate
 * Request field is `company` (not `company_name`) to match the backend DTO.
 */
export const calculateEnhancedSensitivity = async (
  request: EnhancedSensitivityRequest
): Promise<EnhancedSensitivityResponse> => {
  const response = await apiClient.post<EnhancedSensitivityResponse>(
    '/sensitivity/enhanced/calculate',
    request,
    { timeout: 90000 }
  );
  return response.data;
};

// ============================================================================
// Combined Adjusted Sensitivity Analysis (5-parameter with CAP adjustment)
// ============================================================================

/**
 * Run 5-parameter combined sensitivity analysis with automatic CAP adjustment.
 *
 * Varies Revenue, EBITDA Margin, Invested Capital, WACC, and CAP simultaneously.
 * If CAP < WACC or CAP < 0, the backend sets CAP = 1 before running scenarios.
 * Total scenarios = scenario_change_percent.length ^ 5
 *
 * Default payload (as specified):
 *   { company: "...", scenario_change_percent: [-0.06, -0.03, 0, 0.03, 0.06] }
 *   → 5^5 = 3,125 scenarios
 *
 * Backend route: POST /api/sensitivity/enhanced/combined/calculateCombinedAdjusted
 */
export const calculateCombinedAdjustedSensitivity = async (
  request: CombinedAdjustedSensitivityRequest
): Promise<CombinedAdjustedSensitivityResponse> => {
  const response = await apiClient.post<CombinedAdjustedSensitivityResponse>(
    '/sensitivity/enhanced/combined/calculateCombinedAdjusted',
    request,
    { timeout: 120000 }
  );
  return response.data;
};

// ============================================================================
// Speedometer Sensitivity (4-param, returns EV/EBITDA change per year)
// ============================================================================

/**
 * Calculate EV/EBITDA sensitivity for specific slider values.
 *
 * Accepts individual % change values (e.g. revenue=1 means +1%) and
 * returns ev_ebitda_change_percent for each financial year.
 *
 * Backend route: POST /api/sensitivity/enhanced/combined/calculateSpeedometerSensitivityAnalysis
 */
export const calculateSpeedometerSensitivity = async (
  request: SpeedometerSensitivityRequest
): Promise<SpeedometerSensitivityResponse> => {
  const response = await apiClient.post<SpeedometerSensitivityResponse>(
    '/sensitivity/enhanced/combined/calculateSpeedometerSensitivityAnalysis',
    request,
    { timeout: 180000 }
  );
  return response.data;
};

// ============================================================================
// Metric Cards
// ============================================================================

/**
 * Get KPI metric cards for a company (latest year values + CAGR).
 *
 * Returns an array of named cards: Revenue, EBITDA Margin, ROIC, WACC,
 * EV/EBITDA, Market Cap, CAP, Revenue CAGR. Cards with no data are omitted.
 *
 * Backend route: GET /api/v2/financial/company/{name}/metric-cards
 */
export const getCompanyMetricCards = async (
  companyName: string,
  country?: string,
  years: number = 5
): Promise<MetricCardsResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());
  if (country) params.append('country', country);

  const response = await apiClient.get<MetricCardsResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/metric-cards?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// Company Dashboard by Tab
// ============================================================================

/**
 * Get pre-packaged dashboard data for a specific tab.
 *
 * Valid tab values: ``overview``, ``financials``, ``valuation``, ``sensitivity``.
 *
 * Backend route: GET /api/v2/financial/company/{name}/dashboard/{tab}
 */
export const getCompanyDashboard = async (
  companyName: string,
  tab: 'overview' | 'financials' | 'valuation' | 'sensitivity',
  years: number = 10,
  country?: string
): Promise<DashboardTabResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());
  if (country) params.append('country', country);

  const response = await apiClient.get<DashboardTabResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/dashboard/${tab}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get pre-packaged dashboard data for a project tab.
 *
 * Backend route: GET /api/v2/financial/project/{id}/dashboard/{tab}
 */
export const getProjectDashboard = async (
  projectId: string,
  tab: 'overview' | 'financials' | 'valuation' | 'sensitivity',
  years: number = 10
): Promise<ProjectDashboardTabResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());

  const response = await apiClient.get<ProjectDashboardTabResponse>(
    `/v2/financial/project/${projectId}/dashboard/${tab}?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// Trend Analysis
// ============================================================================

/**
 * Get year-on-year trend for a single named metric.
 *
 * Common metric names: ``revenue``, ``ebitda``, ``ebitda_margin``, ``roic``,
 * ``wacc``, ``ev_ebitda``, ``cap``, ``invested_capital``, ``market_cap``.
 *
 * Backend route: GET /api/v2/financial/company/{name}/trend/{metric}
 */
export const getCompanyTrend = async (
  companyName: string,
  metric: string,
  years: number = 10,
  country?: string
): Promise<CompanyTrendResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());
  if (country) params.append('country', country);

  const response = await apiClient.get<CompanyTrendResponse>(
    `/v2/financial/company/${encodeURIComponent(companyName)}/trend/${encodeURIComponent(metric)}?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// Company Compare
// ============================================================================

/**
 * Compare financial KPIs for multiple companies side-by-side.
 *
 * Pass an array of company name strings.
 *
 * Backend route: POST /api/v2/financial/company/compare
 */
export const compareCompanies = async (
  companyNames: string[],
  years: number = 5
): Promise<CompanyCompareResponse> => {
  const response = await apiClient.post<CompanyCompareResponse>(
    `/v2/financial/company/compare?years=${years}`,
    companyNames
  );
  return response.data;
};

// ============================================================================
// Financial Metrics Comparison (peer analysis)
// ============================================================================

/**
 * Get 4-metric (Revenue CAGR, EBITDA, ROIC, WACC) comparison for a company
 * vs named competitors.
 *
 * Backend route: POST /api/calculator/financialMetricsComparison
 */
export const getFinancialMetricsComparison = async (
  request: FinancialMetricsComparisonRequest
): Promise<FinancialMetricsComparisonResponse> => {
  const response = await apiClient.post<FinancialMetricsComparisonResponse>(
    '/calculator/financialMetricsComparison',
    request,
    { timeout: 60000 }
  );
  return response.data;
};

// ============================================================================
// US Company Data (stored in Neo4j)
// ============================================================================

/**
 * Get stored US company data from Neo4j by ticker or name.
 *
 * Backend route: GET /api/finance/us/company/{ticker}
 */
export const getUSCompanyStored = async (ticker: string): Promise<any> => {
  const response = await apiClient.get(`/finance/us/company/${encodeURIComponent(ticker)}`);
  return response.data;
};

/**
 * Get SEC EDGAR filing URLs for a US company.
 *
 * Backend route: GET /api/finance/us/filing-urls/{ticker}
 */
export const getUSFilingUrls = async (
  ticker: string,
  formType: string = '10-K',
  limit: number = 5
): Promise<{ ticker: string; form_type: string; filing_urls: string[] }> => {
  const params = new URLSearchParams({ form_type: formType, limit: limit.toString() });
  const response = await apiClient.get(
    `/finance/us/filing-urls/${encodeURIComponent(ticker)}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get basic company info from SEC EDGAR.
 *
 * Backend route: GET /api/finance/us/company-info/{ticker}
 */
export const getUSCompanyInfo = async (ticker: string): Promise<any> => {
  const response = await apiClient.get(`/finance/us/company-info/${encodeURIComponent(ticker)}`);
  return response.data;
};

// ============================================================================
// Export Combined Adjusted Sensitivity as XLSX
// ============================================================================

/**
 * Download the combined adjusted sensitivity analysis as an Excel file.
 *
 * Backend route: POST /api/sensitivity/enhanced/combined/exportExcelCombinedAdjusted
 */
export const exportCombinedAdjustedSensitivityXlsx = async (
  company: string,
  scenarioChangePercent: number[]
): Promise<void> => {
  const response = await apiClient.post(
    '/sensitivity/enhanced/combined/exportExcelCombinedAdjusted',
    { company, scenario_change_percent: scenarioChangePercent },
    { responseType: 'blob', timeout: 120000 }
  );

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${company}_sensitivity_analysis.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
